import { SelectOption } from '@globaleris/ui.library'
import moment from 'moment'
import {
  EnumDispatchType,
  EnumOperationState,
  EnumSubTipoElemLog,
  EnumTipoElemLog,
  EnumTipoOperacion,
  EnumZonaBalance
} from '../../api/oData/enums'
import {
  ConfigOperacionBalance,
  ElementoLogistico,
  Operacion,
  Reparto
} from '../../api/oData/model'
import {
  BatchRequest,
  BatchRequestItem,
  DispatchRequest,
  OperationRequest
} from '../../api/oData/requests'
import { ApiOperationsLogCreateMassive } from '../../api/Pioneer/model/Operations'
import { enumKeyToValue, enumValueToKey, toLocaleDate } from '../../helpers'
import { formatServerDateSpain } from '../../helpers/formatDateOnly'
import { BalanceZoneStoreModel, CompanyStoreModel, getActiveBalanceZone } from '../../redux'
import NominationModel, {
  AddMassiveNominationModel,
  AddNominationModel,
  AllocationViewModel,
  FlangeViewModel,
  LogisticElementViewModel,
  OperationBalanceConfiguration
} from './nomination.model'
import {
  canCancel,
  canDelete,
  canEdit,
  canSend,
  fetchFlangesItemsOfNomination,
  getGlobalActionsConfiguration
} from './nomination.service'

export const nominationListItemsMapperApiToVM = (
  listNominations: Operacion[],
  listFlanges: Operacion[]
): NominationModel[] => {
  return (
    listNominations && listNominations.map(item => nominationItemMapperApiToVM(item, listFlanges))
  )
}

export const nominationItemMapperApiToVM = (
  item: Operacion,
  listFlanges: Operacion[]
): NominationModel => {
  return (
    item && {
      id: item.id,
      date: moment(formatServerDateSpain(item.fechaOperacion), 'DD-MM-YYYY').toDate(),
      programmingType: enumKeyToValue(EnumTipoOperacion, item.tipoOperacion),
      logisticElement: nominationLogisticElementMapperApiToVM(item.elementoLogistico),
      quantity: item.cantidad,
      sendQuantity: item.cantidadEnvio,
      sendQuantityTSO: item.cantidadTSO,
      allocations: item.repartos,
      state: enumKeyToValue(EnumOperationState, item.estadoOperacion),
      flanges: fetchFlangesItemsOfNomination(item, listFlanges),
      canSend: canSend(
        enumKeyToValue(EnumOperationState, item.estadoOperacion),
        item.fechaOperacion
      ),
      canCancel: canCancel(
        enumKeyToValue(EnumOperationState, item.estadoOperacion),
        item.fechaOperacion
      ),
      canAllocate: true,
      canEdit: canEdit(
        enumKeyToValue(EnumOperationState, item.estadoOperacion),
        item.fechaOperacion
      ),
      canDelete: canDelete(enumKeyToValue(EnumOperationState, item.estadoOperacion)),
      actionsConfiguration: getGlobalActionsConfiguration()
    }
  )
}

export const nominationLogisticElementMapperApiToVM = (
  logisticElement: ElementoLogistico
): LogisticElementViewModel => {
  return (
    logisticElement && {
      id: logisticElement.id,
      description: logisticElement.descripcion,
      type: enumKeyToValue(EnumTipoElemLog, logisticElement.tipoElementoLogistico)
    }
  )
}

export const nominationAllocationMapperApiToVM = (allocation: Reparto): AllocationViewModel =>
  allocation && { id: allocation.id }

export const logisticElementsMapperToDropDown = (items: ElementoLogistico[]): SelectOption[] => {
  return items && items.map(logisticElementMapperToDropDown)
}

export const logisticElementMapperToDropDown = (item: ElementoLogistico): SelectOption => {
  return (
    item && {
      value: item.id,
      label: item.descripcion || ''
    }
  )
}

export const operationsBalanceConfigMapperAPItoModel = (
  items: ConfigOperacionBalance[]
): OperationBalanceConfiguration[] => {
  return items && items.map(operationBalanceConfigMapperAPItoModel)
}

export const operationBalanceConfigMapperAPItoModel = (
  item: ConfigOperacionBalance
): OperationBalanceConfiguration => {
  return (
    item && {
      operationType: enumKeyToValue(EnumTipoOperacion, item.tipoOperacion),
      logisticElementSubType: enumKeyToValue(EnumSubTipoElemLog, item.subtipoElementoLogistico)
    }
  )
}

export const nominationMapperPOST = (
  item: any,
  balanceZone: BalanceZoneStoreModel | undefined,
  company: CompanyStoreModel | undefined
): OperationRequest => {
  return (
    item && {
      fechaOperacion: item.date,
      tipoOperacion: enumValueToKey(EnumTipoOperacion, item.programmingType),
      cantidad: item.quantity,
      estadoOperacion: enumValueToKey(EnumOperationState, item.state),
      zonaBalance: balanceZone?.code,
      elementoLogistico: {
        '@odata.id': 'elementoslogisticos(' + item.logisticElement.id + ')'
      },
      empresa: {
        '@odata.id': 'empresa(' + company?.id + ')'
      }
    }
  )
}

export const nominationCancelMapper = (item: any): OperationRequest => {
  return (
    item && {
      id: item.id,
      tipoOperacion: enumValueToKey(EnumTipoOperacion, item.programmingType),
      estadoOperacion: enumValueToKey(EnumOperationState, item.state),
      cantidad: item.quantity
    }
  )
}

export const nominationItemSendMapper = (
  item: NominationModel,
  dispatchType: EnumDispatchType,
  idCompany: number
): DispatchRequest => {
  return (
    dispatchType && {
      dispatchType: enumValueToKey(EnumDispatchType, dispatchType),
      companyID: idCompany,
      operations: [
        {
          id: item.id,
          operationType: enumValueToKey(EnumTipoOperacion, item.programmingType),
          balanceZone: getActiveBalanceZone()?.code!
        }
      ]
    }
  )
}

export const nominationSendMapper = (
  items: any,
  dispatchType: EnumDispatchType,
  idCompany: number
): DispatchRequest => {
  return (
    items && {
      dispatchType: enumValueToKey(EnumDispatchType, dispatchType),
      companyID: idCompany,
      operations: items.map((item: NominationModel) => {
        return {
          id: item.id,
          operationType: enumValueToKey(EnumTipoOperacion, item.programmingType),
          balanceZone: getActiveBalanceZone()?.code!
        }
      })
    }
  )
}

export const nominationMapperPATCH = (item: any): OperationRequest => {
  return (
    item && {
      id: item.id,
      tipoOperacion: enumValueToKey(EnumTipoOperacion, item.programmingType),
      fechaOperacion: item.date,
      cantidad: item.quantity,
      estadoOperacion: enumValueToKey(EnumOperationState, item.state)
    }
  )
}

export const nominationToDeleteRequest = (item: any): OperationRequest => {
  return (
    item && {
      id: item.id,
      tipoOperacion: enumValueToKey(EnumTipoOperacion, item.programmingType)
    }
  )
}

export const nominationToCancelRequest = (item: any): OperationRequest => {
  return (
    item && {
      id: item.id,
      tipoOperacion: enumValueToKey(EnumTipoOperacion, item.programmingType),
      estadoOperacion: enumValueToKey(EnumOperationState, item.state),
      cantidad: item.quantity
    }
  )
}

export const flangesItemMapperToVM = (item: Operacion): FlangeViewModel => {
  return (
    item && {
      id: item.id,
      operationType: enumKeyToValue(EnumTipoOperacion, item.tipoOperacion),
      quantity: item.cantidad,
      operationState: enumKeyToValue(EnumOperationState, item.estadoOperacion),
      counterpart:
        item.contraparte !== null ? item.contraparte.deSujeto : 'No hay contraparte asociada'
    }
  )
}

export const nominationsToCancelBatchRequest = (items: NominationModel[]): BatchRequest => {
  return (
    items && {
      requests: items.map(nominationToCancelBatchRequestItem)
    }
  )
}

export const nominationToCancelBatchRequestItem = (item: NominationModel): BatchRequestItem => {
  return (
    item && {
      id: `${toLocaleDate(item.date)} - ${enumValueToKey(
        EnumTipoOperacion,
        item.programmingType
      )} - ${item.id}`,
      method: 'PATCH',
      url: `/oData/Operaciones(${item.id}, '${enumValueToKey(
        EnumTipoOperacion,
        item.programmingType
      )}')`,
      headers: {
        'OData-Version': '4.01',
        'Content-Type': 'application/json;odata.metadata=minimal',
        Accept: 'application/json;odata.metadata=minimal'
      },
      body: {
        id: item.id,
        tipoOperacion: enumValueToKey(EnumTipoOperacion, item.programmingType),
        estadoOperacion: enumValueToKey(EnumOperationState, EnumOperationState.PendienteRenvio),
        cantidad: 0
      }
    }
  )
}

export const nominationsToDeleteBatchRequest = (items: NominationModel[]): BatchRequest => {
  return (
    items && {
      requests: items.map(nominationToDeleteBatchRequestItem)
    }
  )
}

export const nominationToDeleteBatchRequestItem = (item: NominationModel): BatchRequestItem => {
  return (
    item && {
      id: `${toLocaleDate(item.date)} - ${enumValueToKey(
        EnumTipoOperacion,
        item.programmingType
      )} - ${item.id}`,
      method: 'DELETE',
      url: `/oData/Operaciones(${item.id}, '${enumValueToKey(
        EnumTipoOperacion,
        item.programmingType
      )}')`,
      headers: {
        'OData-Version': '4.01',
        'Content-Type': 'application/json;odata.metadata=minimal',
        Accept: 'application/json;odata.metadata=minimal'
      }
    }
  )
}

export const nominationsToAddBatchRequest = (
  item: AddMassiveNominationModel,
  companyId: number,
  balanceZoneCode: string
): BatchRequest => {
  let items: AddNominationModel[] = []
  for (
    let current = moment(item.startDate);
    moment(current).isSameOrBefore(moment(item.endDate), 'day');
    current = current.add(1, 'day')
  ) {
    items.push({
      date: current.toDate(),
      programmingType: item.programmingType,
      quantity: item.quantity,
      state: item.state,
      logisticElement: item.logisticElement,
      companyId: companyId,
      balanceZoneCode: balanceZoneCode
    })
  }
  return (
    items && {
      requests: items.map(nominationToAddBatchRequestItem)
    }
  )
}

export const nominationToAddBatchRequestItem = (item: AddNominationModel): BatchRequestItem => {
  return (
    item && {
      id: item.date.toLocaleDateString(),
      method: 'POST',
      url: `/oData/Operaciones`,
      headers: {
        'OData-Version': '4.01',
        'Content-Type': 'application/json;odata.metadata=minimal',
        Accept: 'application/json;odata.metadata=minimal'
      },
      body: {
        fechaOperacion: item.date,
        tipoOperacion: enumKeyToValue(EnumTipoOperacion, item.programmingType),
        cantidad: item.quantity,
        estadoOperacion: enumKeyToValue(EnumOperationState, item.state),
        zonaBalance: item.balanceZoneCode,
        elementoLogistico: {
          '@odata.id': 'elementoslogisticos(' + item.logisticElement?.id + ')'
        },
        empresa: {
          '@odata.id': 'empresa(' + item.companyId + ')'
        }
      }
    }
  )
}

export const mapCreateOrUpdateMassiveRequest = (
  massiveNominationInfo: AddMassiveNominationModel,
  companyId: number,
  balanceZoneId?: number
): ApiOperationsLogCreateMassive => {
  return  {
    startDate: moment(massiveNominationInfo.startDate).format('YYYY-MM-DD'),
    endDate: moment(massiveNominationInfo.endDate).format('YYYY-MM-DD'),
    opType: massiveNominationInfo.programmingType,
    logisticElementId: massiveNominationInfo.logisticElement?.id ?? 0,
    quantity: massiveNominationInfo.quantity ?? 0,
    operationState: massiveNominationInfo.state,
    companyId: companyId,
    balanceZone: balanceZoneId ?? EnumZonaBalance.ESP
  }
}
